import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import VideoCard from '@/components/feature/VideoCard';
import { getVideoById, getVideosByCategory, getCategoryName, videos } from '@/mocks/videos';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id || '');

  if (!video) {
    return <NotFound />;
  }

  const categoryName = getCategoryName(video.categorySlug);
  const relatedVideos = getVideosByCategory(video.categorySlug)
    .filter((v) => v.id !== video.id)
    .slice(0, 4);

  if (relatedVideos.length === 0) {
    const otherVideos = videos.filter((v) => v.id !== video.id).slice(0, 4);
    relatedVideos.push(...otherVideos);
  }

  const articleTitle = `${video.title}在线观看 - 国产${categoryName}高清免费在线观看`;
  const articleDesc = `${video.title}，${video.year}年国产${categoryName}，由${video.director}执导，${video.actors.slice(0, 2).join('、')}等主演。${video.description.substring(0, 100)}`;

  return (
    <>
      <SEOMeta
        title={articleTitle}
        description={articleDesc}
        keywords={`${video.title}在线观看,${video.title}免费观看,${video.title}高清,国产${categoryName}在线观看,${video.actors.join(',')}`}
        canonicalUrl={`https://dywatch.github.io/article/${video.id}`}
        ogType="article"
        ogImage={video.image}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dywatch.github.io/' },
              { '@type': 'ListItem', position: 2, name: categoryName, item: `https://dywatch.github.io/category/${video.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: video.title },
            ],
          }),
        }}
      />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${video.title} - 国产${categoryName}在线观看`,
            description: video.description,
            image: video.image,
            datePublished: `${video.year}-01-01`,
            author: {
              '@type': 'Person',
              name: video.director,
            },
            publisher: {
              '@type': 'Organization',
              name: '国产在线观看',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://dywatch.github.io/article/${video.id}`,
            },
          }),
        }}
      />

      {/* VideoObject Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: video.title,
            description: video.description,
            thumbnailUrl: video.image,
            uploadDate: `${video.year}-01-01`,
            genre: video.tags,
            director: {
              '@type': 'Person',
              name: video.director,
            },
            actor: video.actors.map((name) => ({
              '@type': 'Person',
              name,
            })),
          }),
        }}
      />

      <Navbar />

      <main>
        <div className="px-4 md:px-6 py-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: categoryName, href: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />

          {/* Detail section */}
          <article className="mt-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Poster */}
              <div className="shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={video.image}
                    alt={`${video.title} - 国产${categoryName}高清在线观看`}
                    title={`${video.title} - 国产在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {video.title}
                </h1>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {video.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400 w-5 h-5 flex items-center justify-center"></i>
                    <span className="text-lg font-bold text-foreground-950">{video.rating}</span>
                  </div>
                  <span className="text-sm text-foreground-500">/ 10</span>
                  <span className="text-sm text-foreground-400 ml-2 flex items-center gap-1">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {video.views}
                  </span>
                </div>

                {/* Meta info */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                  <div className="p-3 rounded-lg bg-background-100">
                    <span className="block text-xs text-foreground-500 mb-0.5">{t('article.director')}</span>
                    <span className="text-sm font-semibold text-foreground-950">{video.director}</span>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <span className="block text-xs text-foreground-500 mb-0.5">{t('article.actors')}</span>
                    <span className="text-sm font-semibold text-foreground-950">{video.actors.slice(0, 2).join('、')}</span>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <span className="block text-xs text-foreground-500 mb-0.5">{t('article.region')}</span>
                    <span className="text-sm font-semibold text-foreground-950">{video.region}</span>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <span className="block text-xs text-foreground-500 mb-0.5">{t('article.year')}</span>
                    <span className="text-sm font-semibold text-foreground-950">{video.year}</span>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <span className="block text-xs text-foreground-500 mb-0.5">{t('article.episodes')}</span>
                    <span className="text-sm font-semibold text-foreground-950">{video.episodes}</span>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <span className="block text-xs text-foreground-500 mb-0.5">{t('article.updateInfo')}</span>
                    <span className="text-sm font-semibold text-foreground-950">{video.updateInfo}</span>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h2 className="text-base font-semibold text-foreground-950 mb-2">{t('article.description')}</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill w-5 h-5 flex items-center justify-center"></i>
                    {t('videoCard.play')}
                  </button>
                  <Link
                    to={`/category/${video.categorySlug}`}
                    className="px-8 py-3 bg-background-100 text-foreground-700 rounded-full text-base font-medium hover:bg-background-200/70 transition-colors flex items-center justify-center gap-2 border border-background-200/70 whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    {t('article.backToList')}
                  </Link>
                </div>
              </div>
            </div>
          </article>

          {/* Related videos */}
          {relatedVideos.length > 0 && (
            <section className="mt-10 md:mt-14">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="text-lg font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {t('article.relatedVideos')}
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}